import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { getUser, updateUser } from '../../services/users.service'
import Toast from '../../components/Toast'

function FashionProfile() {
  const { currentUser, loadUserData } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)

  const [formData, setFormData] = useState({
    displayName: '',
    phone: '',
    phoneAlternate: '',
    addresses: [],
    primaryAddressIndex: null,
    notes: ''
  })

  const [newAddress, setNewAddress] = useState({
    street: '',
    city: '',
    postalCode: '',
    province: ''
  })

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true)
      const user = await getUser(currentUser.uid)
      if (user) {
        setFormData({
          displayName: user.displayName || '',
          phone: user.phone || '',
          phoneAlternate: user.phoneAlternate || '',
          addresses: user.addresses || [],
          primaryAddressIndex: user.primaryAddressIndex !== undefined ? user.primaryAddressIndex : null,
          notes: user.notes || ''
        })
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Error al cargar los datos del usuario' })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setLoading(false)
    }
  }, [currentUser])

  useEffect(() => {
    if (currentUser) {
      fetchUserData()
    }
  }, [currentUser, fetchUserData])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!currentUser || saving) return
    setSaving(true)
    try {
      await updateUser(currentUser.uid, formData)
      await loadUserData(currentUser.uid)
      setNotification({ type: 'success', message: 'Perfil actualizado correctamente' })
      setTimeout(() => setNotification(null), 2500)
    } catch (error) {
      setNotification({ type: 'error', message: 'Error al actualizar el perfil' })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setSaving(false)
    }
  }

  const addAddress = () => {
    if (!newAddress.street || !newAddress.city || !newAddress.province) return
    const next = [...formData.addresses, { ...newAddress }]
    setFormData(prev => ({
      ...prev,
      addresses: next,
      primaryAddressIndex: prev.primaryAddressIndex === null ? 0 : prev.primaryAddressIndex
    }))
    setNewAddress({ street: '', city: '', postalCode: '', province: '' })
  }

  const removeAddress = (index) => {
    const next = formData.addresses.filter((_, i) => i !== index)
    let primary = formData.primaryAddressIndex
    if (primary === index) {
      primary = next.length > 0 ? 0 : null
    } else if (primary !== null && primary > index) {
      primary = primary - 1
    }
    setFormData(prev => ({ ...prev, addresses: next, primaryAddressIndex: primary }))
  }

  const setPrimaryAddress = (index) => {
    setFormData(prev => ({ ...prev, primaryAddressIndex: index }))
  }

  if (loading) {
    return (
      <main className="w-full max-w-[1200px] mx-auto px-6 py-12">
        <div className="text-sm text-gray-500">Cargando perfil...</div>
      </main>
    )
  }

  return (
    <main className="w-full max-w-[1200px] mx-auto px-6 py-12">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="mb-10">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Mi Perfil</h1>
        <p className="text-gray-500">Gestiona tu información personal y de contacto.</p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className="lg:col-span-7 bg-white rounded-2xl border border-[#eee] p-8 space-y-8">
          <div className="space-y-2">
            <h2 className="text-xl font-bold">Información Básica</h2>
            <p className="text-sm text-gray-500">Datos asociados a tu cuenta.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Email</label>
              <input
                className="w-full px-4 py-3 bg-[#f8f7f6] border border-[#e8e6e1] rounded-lg text-sm outline-none"
                value={currentUser?.email || ''}
                disabled
              />
              <p className="text-xs text-gray-400">El email no se puede modificar</p>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nombre Completo</label>
              <input
                className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Tu nombre completo"
              />
            </div>
          </div>

          <div className="pt-6 border-t border-[#f1efee] space-y-2">
            <h2 className="text-xl font-bold">Contacto</h2>
            <p className="text-sm text-gray-500">Teléfonos para coordinar entregas.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Teléfono Principal</label>
              <input
                className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+54 11 0000 0000"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Teléfono Alternativo</label>
              <input
                className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
                name="phoneAlternate"
                value={formData.phoneAlternate}
                onChange={handleChange}
                placeholder="+54 11 0000 0000"
              />
            </div>
          </div>

          <div className="pt-6 border-t border-[#f1efee] space-y-3">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">Direcciones</h2>
                <p className="text-sm text-gray-500">Define tu dirección principal para entregas.</p>
              </div>
            </div>

            {formData.addresses.length === 0 ? (
              <p className="text-sm text-gray-500">No tienes direcciones registradas.</p>
            ) : (
              <div className="space-y-3">
                {formData.addresses.map((a, idx) => (
                  <div key={`${a.street}-${idx}`} className="flex items-start justify-between gap-4 border border-[#e8e6e1] rounded-xl p-4">
                    <div>
                      <p className="text-sm font-bold">{a.street}</p>
                      <p className="text-xs text-gray-500">
                        {a.city}, {a.province} {a.postalCode || ''}
                      </p>
                      <button
                        type="button"
                        className={`mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${formData.primaryAddressIndex === idx ? 'text-[#c27e70]' : 'text-gray-400 hover:text-[#c27e70]'}`}
                        onClick={() => setPrimaryAddress(idx)}
                      >
                        <span className="material-symbols-outlined text-[18px]">check_circle</span>
                        {formData.primaryAddressIndex === idx ? 'Principal' : 'Marcar como principal'}
                      </button>
                    </div>
                    <button
                      type="button"
                      className="text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-red-500 transition-colors"
                      onClick={() => removeAddress(idx)}
                    >
                      Eliminar
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 bg-[#f8f7f6] border border-[#e8e6e1] rounded-2xl p-6 space-y-4">
              <p className="text-sm font-bold uppercase tracking-widest text-gray-500">Agregar nueva dirección</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70] md:col-span-2"
                  placeholder="Calle y número"
                  value={newAddress.street}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, street: e.target.value }))}
                />
                <input
                  className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
                  placeholder="Ciudad"
                  value={newAddress.city}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                />
                <input
                  className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
                  placeholder="Provincia"
                  value={newAddress.province}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, province: e.target.value }))}
                />
                <input
                  className="w-full px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
                  placeholder="Código Postal"
                  value={newAddress.postalCode}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                />
              </div>
              <button
                type="button"
                className="bg-white border border-[#e8e6e1] hover:border-[#c27e70] rounded-full px-6 py-3 text-sm font-bold transition-colors"
                onClick={addAddress}
              >
                Agregar
              </button>
            </div>
          </div>
        </section>

        <aside className="lg:col-span-5">
          <div className="sticky top-28 bg-white rounded-2xl border border-[#eee] p-8 shadow-sm space-y-6">
            <div>
              <h2 className="text-xl font-bold mb-2">Notas</h2>
              <p className="text-sm text-gray-500">Información adicional para tus pedidos.</p>
            </div>
            <textarea
              className="w-full min-h-[140px] px-4 py-3 bg-white border border-[#e8e6e1] rounded-lg text-sm outline-none focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70]"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Ej: timbre roto, entregar por la tarde..."
            />

            <button
              type="submit"
              className="w-full bg-[#c27e70] hover:bg-[#a66a5e] text-white px-8 py-4 rounded-full font-extrabold text-sm tracking-wide transition-all transform hover:scale-[1.01] active:scale-95 shadow-xl shadow-[#c27e70]/25"
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </aside>
      </form>
    </main>
  )
}

export default FashionProfile
